import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAdminSession, clearAdminSession, isSuperAdmin, canView, updateStoredAdmin, firstAccessiblePath } from '@/lib/auth';
import {
    LayoutDashboard, CalendarCheck, Users, GraduationCap, MessageSquare,
    ClipboardList, Shield, LogOut, GraduationCap as LogoIcon, Menu, Lock, Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ADMIN_NAV } from '@/constants/testIds';
import { Badge } from '@/components/ui/badge';
import { BrandLogo } from '@/components/BrandLogo';
import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const navItems = [
    { to: '/admin', label: 'Overview', icon: LayoutDashboard, testid: ADMIN_NAV.overview, exact: true, section: 'overview' },
    { to: '/admin/bookings', label: 'Bookings', icon: CalendarCheck, testid: ADMIN_NAV.bookings, section: 'bookings' },
    { to: '/admin/mentors', label: 'Mentors', icon: GraduationCap, testid: ADMIN_NAV.mentors, section: 'mentors' },
    { to: '/admin/students', label: 'Students', icon: Users, testid: ADMIN_NAV.students, section: 'students' },
    { to: '/admin/feedback-form', label: 'Feedback form', icon: ClipboardList, testid: ADMIN_NAV.feedback_form, section: 'feedback_form' },
    { to: '/admin/feedback-submissions', label: 'Feedback', icon: MessageSquare, testid: ADMIN_NAV.feedback_submissions, section: 'feedback_submissions' },
    { to: '/admin/settings', label: 'Settings', icon: Settings, testid: ADMIN_NAV.settings, section: 'settings' },
    { to: '/admin/users', label: 'Admin users', icon: Shield, testid: ADMIN_NAV.users, superOnly: true },
];

const visibleNavItems = () => navItems.filter((i) => (
    i.superOnly ? isSuperAdmin() : canView(i.section)
));

const SidebarContent = ({ pathname, onNav, admin }) => (
    <div className="flex h-full flex-col">
        <div className="flex items-center gap-2 px-5 py-5">
            <BrandLogo size="sm" />
            <span className="rounded-full border border-accent/30 bg-secondary px-2 py-0.5 text-[10px] font-medium text-primary">admin</span>
        </div>
        <nav className="flex-1 space-y-1 px-3">
            {visibleNavItems().map(({ to, label, icon: Icon, testid, exact }) => {
                const active = exact ? pathname === to : pathname === to || pathname.startsWith(to + '/');
                return (
                    <Link
                        key={to}
                        to={to}
                        onClick={onNav}
                        data-testid={testid}
                        className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            active
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                        )}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                    </Link>
                );
            })}
        </nav>
        <div className="border-t border-border/60 p-4">
            <div className="mb-3 flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                    {admin?.name?.[0]?.toUpperCase()}
                </span>
                <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{admin?.name}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {admin?.role === 'super_admin' ? (
                            <Badge className="px-1.5 py-0 text-[10px]">Super admin</Badge>
                        ) : (
                            <Badge variant="outline" className="gap-1 px-1.5 py-0 text-[10px]">
                                <Lock className="h-2.5 w-2.5" /> Limited access
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
            <LogoutButton />
        </div>
    </div>
);

const LogoutButton = () => {
    const navigate = useNavigate();
    return (
        <Button
            variant="ghost"
            className="w-full justify-start"
            data-testid={ADMIN_NAV.logout}
            onClick={() => {
                clearAdminSession();
                navigate('/admin/login');
            }}
        >
            <LogOut className="mr-2 h-4 w-4" />
            Log out
        </Button>
    );
};

export const AdminLayout = ({ children, title, subtitle, actions }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [admin, setAdmin] = useState(() => getAdminSession()?.admin);
    const [open, setOpen] = useState(false);

    // Keep the cached admin (and its permissions) fresh so permission changes made
    // by a super admin apply on the next page load without requiring re-login.
    useEffect(() => {
        let cancelled = false;
        api.get('/admin/me')
            .then(({ data }) => {
                if (cancelled || !data) return;
                updateStoredAdmin(data);
                setAdmin(data);
                // If access to the current section was revoked, bounce to an allowed page.
                const current = navItems.find((i) => (
                    i.exact ? location.pathname === i.to : location.pathname === i.to || location.pathname.startsWith(i.to + '/')
                ));
                if (current && !current.superOnly && !canView(current.section)) {
                    const fallback = firstAccessiblePath();
                    navigate(fallback || '/admin/login', { replace: true });
                }
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    clearAdminSession();
                    navigate('/admin/login', { replace: true });
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <div className="flex min-h-screen bg-background">
            {/* Desktop sidebar */}
            <aside className="hidden w-64 shrink-0 border-r border-border/60 bg-card lg:block">
                <div className="sticky top-0 h-screen">
                    <SidebarContent pathname={location.pathname} admin={admin} />
                </div>
            </aside>

            {/* Mobile sidebar via sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="left" className="w-72 p-0">
                    <SidebarContent pathname={location.pathname} admin={admin} onNav={() => setOpen(false)} />
                </SheetContent>
            </Sheet>

            {/* Content */}
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-border/60 bg-background/95 px-4 backdrop-blur sm:px-6 lg:px-10">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu">
                            <Menu className="h-5 w-5" />
                        </Button>
                        <div className="min-w-0">
                            <h1 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
                            {subtitle && (
                                <p className="text-sm text-muted-foreground">{subtitle}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">{actions}</div>
                </div>
                <main className="px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
                    {children}
                </main>
            </div>
        </div>
    );
};
